import { featureCollection, lineString, polygon } from '@turf/helpers'
import $ from 'jquery'
import OLFormatGeoJSON from 'ol/format/GeoJSON'

import type { Position } from '@turf/helpers'
import type { DrawEvent as OLInteractionDrawEvent } from 'ol/interaction/Draw'
import type { FlatStyle as OLStyleFlat } from 'ol/style/flat'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { ShowWhatsHere, UseWhatsHereInfoPanel } from 'drawer/tabs/info/WhatsHereUtils'
import PoziMeasure from 'drawer/tabs/tools/measure'
import PoziMap from 'map/map'
import { targetSet } from 'utils/target'

const strokeLineDash = [20, 15]
const strokeFillColour = 'white'
const strokeOutlineColour = 'rgba(0, 0, 0, 0.2)'

export const selectStyle: OLStyleFlat[] = [
  {
    'stroke-color': strokeOutlineColour,
    'stroke-line-dash': strokeLineDash,
    'stroke-width': 10
  },
  {
    'stroke-color': strokeFillColour,
    'stroke-line-dash': strokeLineDash,
    'stroke-width': 5
  }
]

export const selectStyleDraw: OLStyleFlat[] = [
  ...selectStyle,
  {
    // Outer circle with semi-transparent outline
    'circle-radius': 7,
    'circle-stroke-color': strokeOutlineColour,
    'circle-stroke-width': 5
  },
  {
    // Inner circle with opaque white fill
    'circle-radius': 4,
    'circle-fill-color': strokeFillColour
  }
]

// Create transparency gradient in halo of circle
const circleHaloColour = 'rgba(150,150, 150, 0.1)'
const circleHaloStyle: OLStyleFlat[] = [
  {
    'circle-radius': 12,
    'circle-stroke-color': circleHaloColour,
    'circle-stroke-width': 16
  },
  {
    'circle-radius': 12,
    'circle-stroke-color': circleHaloColour,
    'circle-stroke-width': 13
  },
  {
    'circle-radius': 12,
    'circle-stroke-color': circleHaloColour,
    'circle-stroke-width': 10
  },
  {
    'circle-radius': 12,
    'circle-stroke-color': circleHaloColour,
    'circle-stroke-width': 9
  }
]

export const selectStyleCurrentTarget: OLStyleFlat[] = [
  ...selectStyle,
  ...circleHaloStyle,
  {
    // Inner circle with white stroke
    'circle-radius': 12,
    'circle-stroke-color': strokeFillColour,
    'circle-stroke-width': 3
  }
]

/**
 * Pozi Multi Select module.
 * Handles multi property lookups, and other related logic.
 */
export default function PoziSelect () {
  // Adapters
  const adapters = {
    init: function () {
      return init()
    },
    setTarget: function (evt: OLInteractionDrawEvent) {
      return setTarget(evt)
    }
  }

  const app = PoziUtils.getApp()

  // ============================================================
  // Public Functions
  // ============================================================

  /**
   * Show button if licensed.
   * Wireup events
   */
  const init = function () {
    const showSelectTools = Model.get('config.layout.drawer.tabs.tools.polygonselect.visible', true)
    if (showSelectTools) {
      wireup()
    } else {
      $('#lineSelectTool,#regionSelectTool').hide()
    }
  }

  // ============================================================
  // Private Functions
  // ============================================================
  /**
   * Wireup click
   */
  const wireup = function () {
    $('#lineSelectTool').on('click', function () {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'Select by line button',
        action: 'measure_or_select_by_line_click'
      })
      start('line')
    })

    $('#regionSelectTool').on('click', function () {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'Select by area button',
        action: 'measure_or_select_by_polygon_click'
      })
      start('polygon')
    })
  }

  /**
   * Start the multi select tool (line or polygon)
   */
  const start = function (type = '') {
    const validTypes = ['line', 'polygon']

    if (!validTypes.includes(type)) {
      console.warn('PoziSelect - invalid type:', type)
      return
    }

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: `${type} select tool`,
      action: `Click select by ${type} button`
    })

    PoziMap().removeMarker()

    console.log('_____________________')
    console.log('PoziSelect: Start...')

    let measuretype

    switch (type) {
      case 'line':
        measuretype = 'distance'
        break
      case 'polygon':
        measuretype = 'area'
        break
    }

    PoziMeasure().start({
      measuretype,
      callback: end,
      style: selectStyle,
      styleDraw: selectStyleDraw,
      showtooltip: false
    })
  }

  /**
   * End the multi select tool
   * @param {event} evt
   */
  const end = function (evt: OLInteractionDrawEvent) {
    console.debug('PoziSelect - end', { ...evt })
    const featureCollectionT = setTarget(evt)

    // Remove drawn line/region as this will be handled by the current target layer
    PoziMeasure().clear()

    if (UseWhatsHereInfoPanel()) {
      ShowWhatsHere(featureCollectionT)
    } else {
      app.activateMode('default') // Triggers card expansion.
    }
  }

  const setTarget = function (evt: OLInteractionDrawEvent) {
    console.log('PoziSelect: set target...')
    const sourceProj = app.map.getView().getProjection()

    const olFeature = evt.feature

    if (!olFeature) {
      console.log('Select - setTarget: no feature. Not doing anything.', evt)
      return
    }

    const olGeom = olFeature.getGeometry()?.clone().transform(sourceProj, 'EPSG:4326')
    if (!olGeom) {
      console.log('Select - setTarget: no geom. Not doing anything.', olFeature, evt)
      return
    }

    const olGeomType = olGeom.getType().toLowerCase()
    const olFormatGeoJSON = new OLFormatGeoJSON()

    // TODO: get the correct type that supports .coordinates
    const olGeomObj: any = olFormatGeoJSON.writeGeometryObject(olGeom, {})

    // TODO: get the correct type
    let geomT

    switch (olGeomType) {
      case 'linestring':
        geomT = lineString(olGeomObj.coordinates as Position[])
        break
      case 'polygon':
        geomT = polygon(olGeomObj.coordinates as Position[][])
        break
    }

    if (!geomT) {
      console.warn('PoziSelect - invalid geometry type:', olGeomType, olGeom)
      return
    }

    // @ts-expect-error
    const featureCollectionT = featureCollection([geomT])
    targetSet(featureCollectionT)

    return featureCollectionT
  }

  // Return adapters (must be at end of adapter)
  return adapters
}
